import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import IosShareIcon from "@mui/icons-material/IosShare";
import AddToHomeScreenIcon from "@mui/icons-material/AddToHomeScreen";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "i18n-fe";

// True when the app is already running as an installed PWA (standalone),
// in which case there's nothing to install.
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// iOS Safari never fires beforeinstallprompt, so it needs the manual
// Add-to-Home-Screen instructions. iPadOS 13+ reports a Mac UA, so fall
// back to touch detection for it.
function isIos() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS =
    window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

// #296 — install / Add-to-Home-Screen affordance. Captures
// beforeinstallprompt and triggers the native prompt where supported;
// falls back to iOS instructions; renders nothing once installed.
export default function InstallButton() {
  const { t } = useTranslation("common");
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosOpen, setIosOpen] = useState(false);
  const [installed, setInstalled] = useState(false);
  // Snapshot standalone once at mount; it doesn't change within a session.
  const [standalone] = useState(isStandalone);
  const ios = isIos();

  useEffect(() => {
    const onBeforeInstall = (event) => {
      // Stop Chrome's default mini-infobar so we control when to prompt.
      event.preventDefault();
      setDeferredPrompt(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // AC3 — already installed: no install affordance.
  if (standalone || installed) return null;

  const canPrompt = Boolean(deferredPrompt);
  // Show only when there's a way to install: a captured native prompt, or
  // iOS (which needs the manual instructions). Otherwise stay hidden.
  if (!canPrompt && !ios) return null;

  const handleClick = async () => {
    if (canPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      // A prompt can only be used once.
      setDeferredPrompt(null);
    } else {
      setIosOpen(true);
    }
  };

  const steps = [
    { icon: <IosShareIcon />, text: t("install.iosStep1") },
    { icon: <AddToHomeScreenIcon />, text: t("install.iosStep2") },
    { icon: <CheckCircleOutlineIcon />, text: t("install.iosStep3") },
  ];

  return (
    <>
      <Button
        data-testid="install-button"
        color="inherit"
        size="small"
        startIcon={<InstallMobileIcon />}
        onClick={handleClick}
        // On the dark AppBar; inherit keeps the label white and legible.
        sx={{ color: "inherit", textTransform: "none", fontWeight: 600 }}
      >
        {t("install.button")}
      </Button>
      <Dialog
        open={iosOpen}
        onClose={() => setIosOpen(false)}
        data-testid="install-ios-dialog"
      >
        <DialogTitle>{t("install.iosTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("install.iosIntro")}
          </Typography>
          <List dense>
            {steps.map((step) => (
              <ListItem key={step.text} disableGutters>
                <ListItemIcon sx={{ minWidth: 40 }}>{step.icon}</ListItemIcon>
                <ListItemText primary={step.text} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          {/* inherit so the action stays legible on the dark dialog paper */}
          <Button color="inherit" onClick={() => setIosOpen(false)}>
            {t("install.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
